import React, {
	useCallback,
	cloneElement,
	useState,
	type KeyboardEvent,
	type ReactElement,
	useEffect,
} from 'react';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project';
import { isClientSideValidationError } from '@atlassian/jira-business-error-handling';
import {
	useClipboard,
	type ListIssueFieldTransformedUnionForCopyPaste,
	type ClipboardItem,
} from '@atlassian/jira-business-fields/clipboard';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useFlagsService, toFlagId } from '@atlassian/jira-flags';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useCellActions } from '../cell-navigation';
import type { ListViewControllerData } from '../list-view-issues/types';

const FLAG_ID = toFlagId('LIST_COPY_PASTE_FLAG');

const UNSETTABLE_FIELD_TYPES = {
	issuekey: true,
	issuetype: true,
	priority: true,
	status: true,
	parent: true,
};

const canBeReset = (field: BusinessIssueField) => {
	const isEditable = field.fieldConfig?.isEditable ?? false;
	const isRequired = field.fieldConfig?.isRequired ?? false;

	if (field.type != null && field.type in UNSETTABLE_FIELD_TYPES) {
		return false;
	}

	return isEditable && !isRequired;
};

type ReportedErrorInput = {
	action: string;
	title: string;
	message: string;
	eventName?: string;
	error?: Error;
};
export const ClipboardController = ({
	children,
	issue,
	field,
	isSelected,
	onKeyDown,
}: {
	children: ReactElement;
	issue?: ListViewControllerData;
	field: BusinessIssueField;
	isSelected: boolean;
	onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}) => {
	const issueKey = issue?.key;
	const transformedField = issue?.fields?.find((f) => {
		const fieldId = field.id === 'key' ? 'issuekey' : field.id;
		return f.fieldId === fieldId;
	});
	const { dismissFlag, showFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [clipboard, setClipboard] = useState<ClipboardItem | null>(null);
	const [isCutting, setIsCutting] = useState<boolean>(false);

	const { setCopiedFromCell, clearCopiedFromCell } = useCellActions();

	const isEditable = field.fieldConfig?.isEditable ?? true;

	const { copyCell, renderPasteHandlerComponent, renderCutHandlerComponent } = useClipboard(
		{ key: issueKey, issueId: issue?.issueId, issueType: issue?.issuetype },
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		transformedField as ListIssueFieldTransformedUnionForCopyPaste | undefined,
	);

	const reportError = useCallback(
		({ action, title, eventName, message, error }: ReportedErrorInput) => {
			showFlag({
				id: FLAG_ID,
				type: 'error',
				title,
				description: message,
			});

			const isValidationError = isClientSideValidationError(error);
			// We are handling validation errors so there is no need to log them to sentry.
			// Conscious of cost though, let's revisit this to see if it's worth keeping it.
			// https://jbusiness.atlassian.net/browse/FLINGO-824
			if (isValidationError) {
				fireTrackAnalytics(createAnalyticsEvent({}), eventName || '', {
					fieldType: field.type,
					hasCopied: false,
					reason: error.message,
				});
			} else {
				const ugcSafeMessage = `Failed to ${action} a field of type ${field.type}`;
				fireErrorAnalytics({
					meta: {
						id: 'jiraClipboardController',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: {
						message: ugcSafeMessage,
						fieldType: field.type,
					},
					error: new Error(ugcSafeMessage),
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
		[showFlag, createAnalyticsEvent, field.type],
	);

	const onPaste = useCallback(
		(success: boolean) => {
			dismissFlag(FLAG_ID);
			setClipboard(null);

			if (success) {
				fireTrackAnalytics(createAnalyticsEvent({}), 'cell pasted', {
					fieldType: field.type,
				});
			} else {
				reportError({
					action: 'paste',
					title: 'Unable to paste',
					message: `Failed to paste into ${field.name}`,
				});
			}
		},
		[createAnalyticsEvent, dismissFlag, field.name, field.type, reportError],
	);

	const onCut = useCallback(
		(success: boolean) => {
			dismissFlag(FLAG_ID);
			setIsCutting(false);

			if (!success) {
				reportError({
					action: 'cut',
					title: 'Unable to cut',
					message: `Failed to cut ${field.name}, copied instead`,
				});
			}
		},
		[dismissFlag, field.name, reportError],
	);

	const paste = useCallback(
		(event: ClipboardEvent) => {
			const data = event.clipboardData;

			setClipboard({
				plainText: data?.getData('text/plain'),
				html: data?.getData('text/html'),
			});
		},
		[setClipboard],
	);

	const copy = useCallback(async () => {
		dismissFlag(FLAG_ID);
		try {
			setCopiedFromCell(field.id, issue?.issueId);
			await copyCell();

			fireTrackAnalytics(createAnalyticsEvent({}), 'cell copied', {
				fieldType: field.type,
				hasCopied: true,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			reportError({
				action: 'copy',
				title: 'Unable to copy',
				message: `Failed to copy ${field.name}`,
				eventName: 'cell copied',
				error,
			});
			clearCopiedFromCell();
		}
	}, [
		clearCopiedFromCell,
		copyCell,
		createAnalyticsEvent,
		dismissFlag,
		field.id,
		field.name,
		field.type,
		issue?.issueId,
		reportError,
		setCopiedFromCell,
	]);

	const cut = useCallback(async () => {
		dismissFlag(FLAG_ID);
		try {
			setCopiedFromCell(field.id, issue?.issueId);
			await copyCell();

			if (canBeReset(field)) {
				setIsCutting(true);
			}

			fireTrackAnalytics(createAnalyticsEvent({}), 'cell cut', {
				fieldType: field.type,
				canBeReset: canBeReset(field),
				hasCopied: true,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			reportError({
				action: 'cut',
				title: 'Unable to cut',
				message: `Failed to cut ${field.name}`,
				eventName: 'cell cut',
				error,
			});
			clearCopiedFromCell();
		}
	}, [
		dismissFlag,
		setCopiedFromCell,
		field,
		issue?.issueId,
		copyCell,
		createAnalyticsEvent,
		reportError,
		clearCopiedFromCell,
	]);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLElement>) => {
			if (isSelected && event.metaKey && event.key === 'c') {
				copy();
				return;
			}

			if (isSelected && event.metaKey && event.key === 'x') {
				cut();
				return;
			}

			onKeyDown(event);
		},
		[copy, cut, isSelected, onKeyDown],
	);

	useEffect(() => {
		if (isSelected) {
			document.addEventListener('paste', paste, true);
		} else {
			document.removeEventListener('paste', paste, true);
		}

		return () => {
			document.removeEventListener('paste', paste, true);
		};
	}, [isSelected, paste]);

	return (
		<>
			{cloneElement(children, {
				onKeyDown: handleKeyDown,
			})}
			{clipboard &&
				isEditable &&
				renderPasteHandlerComponent({ clipboard, onPaste, onBeforePaste: clearCopiedFromCell })}
			{isCutting && renderCutHandlerComponent({ onCut })}
		</>
	);
};
